import React, { useMemo, useState } from "react";
import { authFetch } from "../../api/api";
import Button from "../../UI/Button";
import Modal from "../../UI/Modal";
import styles from "./OrderFinances.module.css";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;
const ACCOUNTING_API = `${BASE_URL}/api/accounting/accounting-records/`;

const getList = (data) => {
if (Array.isArray(data)) return data;
if (Array.isArray(data?.results)) return data.results;
if (Array.isArray(data?.data)) return data.data;
return [];
};

const formatMoney = (value) =>
Number(value || 0).toLocaleString("ru-RU", {
minimumFractionDigits: 2,
maximumFractionDigits: 2,
});

const formatDate = (value) => {
if (!value) return "—";

const date = new Date(value);
return Number.isNaN(date.getTime())
? value
: date.toLocaleDateString("ru-RU");
};

const getErrorMessage = async (response, fallback) => {
try {
const data = await response.json();


if (data?.error) return data.error;
if (data?.detail) return data.detail;

return Object.entries(data || {})
  .map(([key, value]) => {
    const message = Array.isArray(value)
      ? value.join(", ")
      : String(value);

    return `${key}: ${message}`;
  })
  .join("; ") || fallback;


} catch {
return fallback;
}
};

const getAllocatedAmount = (record) =>
(record.allocations || []).reduce(
(sum, allocation) => sum + Number(allocation.amount || 0),
0
);

const getRemainingAmount = (record) =>
Math.max(
0,
Number(record.money_sum || 0) - getAllocatedAmount(record)
);

const uploadAllocationDocuments = async (allocationId, files) => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append("files", file);
  });

  const response = await authFetch(
    `${BASE_URL}/api/accounting/allocations/${allocationId}/documents/`,
    {
      method: "POST",
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error(
      await getErrorMessage(
        response,
        "Не удалось загрузить подтверждающие документы."
      )
    );
  }

  return response;
};



export default function ClientPaymentAllocationModal({
  isOpen,
  onClose,
  orderId,
  clientId,
  accountingRecords = [],
  onSaved,
}) {
const [selectedRecord, setSelectedRecord] = useState(null);
const [amount, setAmount] = useState("");
const [date, setDate] = useState("");
const [comment, setComment] = useState("");
const [saving, setSaving] = useState(false);
const [error, setError] = useState("");

const [proofFiles, setProofFiles] = useState([]);


const [showCreatePayment, setShowCreatePayment] = useState(false);
const [creatingPayment, setCreatingPayment] = useState(false);

const [newPayment, setNewPayment] = useState({
  date: new Date().toISOString().slice(0, 10),
  payment_agent: "",
  money_sum: "",
  comment: "",
  payment_status: "PENDING",
});

// Если назначение сохранилось, но загрузка файлов завершилась ошибкой,
// храним ID назначения, чтобы повторная отправка не создала дубликат.
const [pendingUploadAllocationId, setPendingUploadAllocationId] =
  useState(null);

const availablePayments = useMemo(
  () =>
    accountingRecords
      .filter(
        (record) =>
          String(record.client) === String(clientId) &&
          getRemainingAmount(record) > 0.005
      )
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      }),
  [accountingRecords, clientId]
);

const openAllocationForm = (record) => {
  setSelectedRecord(record);
  setAmount(getRemainingAmount(record).toFixed(2));
  setDate(record.date || "");
  setComment("");
  setProofFiles([]);
  setPendingUploadAllocationId(null);
  setError("");
};

const closeAllocationForm = () => {
  if (saving) return;

  setSelectedRecord(null);
  setAmount("");
  setDate("");
  setComment("");
  setProofFiles([]);
  setPendingUploadAllocationId(null);
  setError("");
};

const saveAllocation = async (event) => {
event.preventDefault();


if (!selectedRecord) return;

if (pendingUploadAllocationId) {
  if (proofFiles.length === 0) {
    setError("Выберите документы для загрузки.");
    return;
  }

  setSaving(true);
  setError("");

  try {
    await uploadAllocationDocuments(
      pendingUploadAllocationId,
      proofFiles
    );

    setPendingUploadAllocationId(null);
    setSelectedRecord(null);
    setAmount("");
    setDate("");
    setComment("");
    setProofFiles("");

    onClose();

    if (onSaved) {
      await onSaved();
    }
  } catch (uploadError) {
    console.error(uploadError);
    setError(
      `Назначение уже сохранено, но загрузить документы не удалось. ` +
        `${uploadError.message || "Попробуйте ещё раз."}`
    );
  } finally {
    setSaving(false);
  }

  return;
}


const numericAmount = Number(amount);
const remaining = getRemainingAmount(selectedRecord);

if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
  setError("Сумма должна быть больше нуля.");
  return;
}

if (numericAmount > remaining + 0.005) {
  setError(
    `Сумма превышает остаток платежа: ¥${formatMoney(remaining)}.`
  );
  return;
}

setSaving(true);
setError("");

let createdAllocationId = null;
let allocationWasSaved = false;

try {
  const detailUrl = `${ACCOUNTING_API}${selectedRecord.id}/`;

  // Получаем актуальную запись перед сохранением распределений.
  const detailResponse = await authFetch(detailUrl);

  if (!detailResponse.ok) {
    throw new Error(
      await getErrorMessage(
        detailResponse,
        "Не удалось загрузить платёж клиента."
      )
    );
  }

  const latestRecord = await detailResponse.json();
  const allocations = Array.isArray(latestRecord.allocations)
    ? latestRecord.allocations
    : [];

  const latestAllocatedAmount = allocations.reduce(
    (sum, allocation) => sum + Number(allocation.amount || 0),
    0
  );

  const latestRemaining = Math.max(
    0,
    Number(latestRecord.money_sum || 0) - latestAllocatedAmount
  );

  if (numericAmount > latestRemaining + 0.005) {
    throw new Error(
      `Остаток платежа изменился. Сейчас доступно: ¥${formatMoney(
        latestRemaining
      )}. Закройте модалку и откройте её снова.`
    );
  }

  // Сохраняем существующие распределения и добавляем новое.
  const updatedAllocations = allocations.map((allocation) => ({
    id: allocation.id,
    order: Number(allocation.order?.id ?? allocation.order),
    amount: String(allocation.amount),
    date: allocation.date || null,
    comment: allocation.comment || "",
    our_allocations: (allocation.our_allocations || []).map((item) => ({
      id: item.id,
      order: Number(item.order?.id ?? item.order),
      amount: String(item.amount),
      date: item.date || null,
      comment: item.comment || "",
    })),
  }));

  updatedAllocations.push({
    order: Number(orderId),
    amount: numericAmount.toFixed(2),
    date: date || null,
    comment: comment.trim(),
    our_allocations: [],
  });





    const saveResponse = await authFetch(detailUrl, {
    method: "PATCH",
    headers: {
        "Content-Type": "application/json",
    },
    body: JSON.stringify({
        allocations: updatedAllocations,
    }),
    });

    if (!saveResponse.ok) {
    throw new Error(
        await getErrorMessage(
        saveResponse,
        "Не удалось добавить назначение платежа."
        )
    );
    }

    allocationWasSaved = true;

    // Документы прикрепляются к созданному назначению,
    // поэтому сначала нужно получить его ID.
    if (proofFiles.length > 0) {
    let updatedRecord = null;

    try {
        updatedRecord = await saveResponse.json();
    } catch {
        // Если тело ответа не удалось прочитать, ниже попробуем GET.
    }

    const existingIds = new Set(
        allocations.map((allocation) => String(allocation.id))
    );

    const findNewAllocation = (record) =>
        (record?.allocations || []).find(
        (allocation) =>
            allocation.id != null &&
            !existingIds.has(String(allocation.id))
        );

    let createdAllocation = findNewAllocation(updatedRecord);

    // Резервный вариант — перечитать актуальную запись с сервера.
    if (!createdAllocation?.id) {
        const refreshResponse = await authFetch(detailUrl);

        if (refreshResponse.ok) {
        const refreshedRecord = await refreshResponse.json();
        createdAllocation = findNewAllocation(refreshedRecord);
        }
    }

    if (!createdAllocation?.id) {
        throw new Error(
        "Назначение создано, но не удалось определить его ID для загрузки документов. " +
            "Обновите запись и проверьте назначение, прежде чем создавать его повторно."
        );
    }

    createdAllocationId = createdAllocation.id;
    setPendingUploadAllocationId(createdAllocationId);

    // Загружаем подтверждения именно к новому назначению.
    await uploadAllocationDocuments(
        createdAllocationId,
        proofFiles
    );

    setPendingUploadAllocationId(null);
    }

    // Успешное сохранение назначения и загрузка документов.
    setSelectedRecord(null);
    setAmount("");
    setDate("");
    setComment("");
    setProofFiles([]);

    onClose();

    if (onSaved) {
    await onSaved();
    }




    } catch (saveError) {
    console.error(saveError);

    if (createdAllocationId) {
        // Назначение уже существует. Оставляем форму открытой,
        // чтобы пользователь мог повторить только загрузку документов.
        setPendingUploadAllocationId(createdAllocationId);
        setError(
        `Назначение уже сохранено, но документы не загрузились. ` +
            `${saveError.message || "Попробуйте повторить загрузку."}`
        );
    } else if (allocationWasSaved) {
        setError(
        saveError.message ||
            "Назначение сохранено, но возникла ошибка при обработке документов. " +
            "Проверьте назначение, прежде чем повторять сохранение."
        );
    } else {
        setError(
        saveError.message || "Не удалось распределить платёж."
        );
    }
    } finally {
    setSaving(false);
    }

};

const createClientPayment = async (event) => {
  event.preventDefault();

  if (!clientId) {
    setError("Не удалось определить клиента текущего заказа.");
    return;
  }

  const moneySum = Number(newPayment.money_sum);

  if (!Number.isFinite(moneySum) || moneySum <= 0) {
    setError("Сумма должна быть больше нуля.");
    return;
  }

  setCreatingPayment(true);
  setError("");

  try {
    const response = await authFetch(ACCOUNTING_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        client: Number(clientId),
        date: newPayment.date || null,
        payment_agent: newPayment.payment_agent.trim(),
        money_sum: moneySum.toFixed(2),
        comment: newPayment.comment.trim(),
        payment_status: newPayment.payment_status,
      }),
    });

    if (!response.ok) {
      throw new Error(
        await getErrorMessage(response, "Не удалось создать платёж.")
      );
    }

    setNewPayment({
      date: new Date().toISOString().slice(0, 10),
      payment_agent: "",
      money_sum: "",
      comment: "",
      payment_status: "PENDING",
    });

    setShowCreatePayment(false);

    if (onSaved) {
      await onSaved();
    }
  } catch (err) {
    setError(err.message || "Ошибка создания платежа.");
  } finally {
    setCreatingPayment(false);
  }
};

if (!isOpen) return null;

return (
<Modal
isOpen={true}
onClose={() => {
if (selectedRecord) {
closeAllocationForm();
} else if (!saving) {
onClose();
}
}}
> <div className="w-full min-w-[320px] max-w-3xl space-y-4 p-4"> <div className="flex items-start justify-between gap-3"> <div> <h2 className="text-xl font-bold">
Добавить платёж клиента </h2> <p className="mt-1 text-sm text-gray-600">
Выберите платёж с нераспределённым остатком и назначьте его
текущему заказу. </p> </div>


      <Button
        type="button"
        variant="secondary"
        disabled={saving}
        onClick={() => {
          if (selectedRecord) {
            closeAllocationForm();
          } else {
            onClose();
          }
        }}
      >
        Закрыть
      </Button>
    </div>

    {!selectedRecord && (
      <div className="space-y-3">
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            setShowCreatePayment((value) => !value);
            setError("");
          }}
        >
          {showCreatePayment
            ? "Отмена создания"
            : "＋ Создать новый платёж клиента"}
        </Button>

        {showCreatePayment && (
          <form
            onSubmit={createClientPayment}
            className="space-y-3 rounded-lg border p-4"
          >
            <p className="font-semibold">Новый платёж клиента</p>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Дата платежа
              </label>
              <input
                type="date"
                value={newPayment.date}
                onChange={(e) =>
                  setNewPayment({
                    ...newPayment,
                    date: e.target.value,
                  })
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Сумма платежа
              </label>
                <input
                type="number"
                onWheel={(e) => e.currentTarget.blur()}
                min="0.01"
                step="0.01"
                required
                value={newPayment.money_sum}
                onChange={(e) =>
                  setNewPayment({
                    ...newPayment,
                    money_sum: e.target.value,
                  })
                }
                className={`${styles.noNumberSpinner} w-full rounded border px-3 py-2`}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Платёж через
              </label>
              <input
                type="text"
                value={newPayment.payment_agent}
                onChange={(e) =>
                  setNewPayment({
                    ...newPayment,
                    payment_agent: e.target.value,
                  })
                }
                className="w-full rounded border px-3 py-2"
                placeholder="Например, Alina или Shangtong"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Статус оплаты
              </label>
              <select
                value={newPayment.payment_status}
                onChange={(e) =>
                  setNewPayment({
                    ...newPayment,
                    payment_status: e.target.value,
                  })
                }
                className="w-full rounded border px-3 py-2"
              >
                <option value="PENDING">Не оплачен</option>
                <option value="PAID">Оплачен</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Комментарий
              </label>
              <textarea
                rows={3}
                value={newPayment.comment}
                onChange={(e) =>
                  setNewPayment({
                    ...newPayment,
                    comment: e.target.value,
                  })
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            {error && (
              <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={creatingPayment}
            >
              {creatingPayment ? "Сохранение..." : "Сохранить платёж"}
            </Button>
          </form>
        )}
      </div>
    )}



    {selectedRecord ? (
      <form onSubmit={saveAllocation} className="space-y-4">
        <button
          type="button"
          className="text-sm text-blue-600 underline"
          disabled={saving}
          onClick={closeAllocationForm}
        >
          ← К списку платежей
        </button>

        <div className="rounded-lg border bg-gray-50 p-4">
          <p className="font-semibold">
            Платёж Accounting №{selectedRecord.id}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            Клиент: {selectedRecord.client_name || "—"}
          </p>
          <p className="text-sm text-gray-600">
            Дата: {formatDate(selectedRecord.date)}
          </p>
          <p className="mt-2 text-sm">
            Общая сумма: ¥{formatMoney(selectedRecord.money_sum)}
          </p>
          <p className="text-sm">
            Уже распределено: ¥
            {formatMoney(getAllocatedAmount(selectedRecord))}
          </p>
          <p className="font-semibold text-green-700">
            Остаток: ¥{formatMoney(getRemainingAmount(selectedRecord))}
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">
            Сумма назначения
          </label>
          <input
            type="number"
            onWheel={(e) => e.currentTarget.blur()}
            min="0.01"
            max={getRemainingAmount(selectedRecord)}
            step="0.01"
            required
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className={`${styles.noNumberSpinner} w-full rounded border px-3 py-2`}
          />
          <button
            type="button"
            className="mt-1 text-sm text-blue-600 underline"
            onClick={() =>
              setAmount(getRemainingAmount(selectedRecord).toFixed(2))
            }
          >
            Использовать весь остаток
          </button>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">
            Дата назначения
          </label>
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="w-full rounded border px-3 py-2"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">
            Комментарий
          </label>
          <textarea
            rows={3}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            className="w-full rounded border px-3 py-2"
          />
        </div>




        <div>
        <label className="mb-1 block text-sm font-medium">
            Подтверждающие документы
        </label>

        <input
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
            disabled={saving || pendingUploadAllocationId !== null}
            onChange={(event) => {
            setProofFiles(Array.from(event.target.files || []));
            setError("");
            event.target.value = "";
            }}
            className="block w-full rounded border px-3 py-2 text-sm"
        />

        <p className="mt-1 text-xs text-gray-500">
            Можно выбрать несколько файлов: PDF, JPG, PNG или WEBP.
        </p>

        {proofFiles.length > 0 && (
            <div className="mt-2 rounded border bg-gray-50 p-3">
            <p className="mb-2 text-sm font-medium">
                Выбранные файлы ({proofFiles.length})
            </p>

            <ul className="space-y-2">
                {proofFiles.map((file, index) => (
                <li
                    key={`${file.name}-${file.lastModified}-${file.size}`}
                    className="flex items-center justify-between gap-3 text-sm"
                >
                    <span className="min-w-0 break-all">
                    {file.name}
                    <span className="ml-2 text-gray-500">
                        ({(file.size / 1024 / 1024).toFixed(2)} МБ)
                    </span>
                    </span>

                    <button
                    type="button"
                    className="shrink-0 text-red-600 hover:underline disabled:text-gray-400"
                    disabled={
                        saving || pendingUploadAllocationId !== null
                    }
                    onClick={() => {
                        setProofFiles((current) =>
                        current.filter((_, fileIndex) => fileIndex !== index)
                        );
                    }}
                    >
                    Удалить
                    </button>
                </li>
                ))}
            </ul>
            </div>
        )}
        </div>



        {error && (
          <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={closeAllocationForm}
          >
            Отмена
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving
            ? pendingUploadAllocationId
                ? "Загрузка документов..."
                : "Сохранение..."
            : pendingUploadAllocationId
                ? "Повторить загрузку документов"
                : "Добавить назначение"}
          </Button>
        </div>
      </form>
    ) : (
      <div className="max-h-[65vh] space-y-3 overflow-y-auto">
        {availablePayments.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-gray-500">
            Нет платежей с нераспределённым остатком.
          </div>
        ) : (
          availablePayments.map((record) => {
            const allocated = getAllocatedAmount(record);
            const remaining = getRemainingAmount(record);

            return (
              <div
                key={record.id}
                className="rounded-lg border bg-white p-4 shadow-sm"
              >
                <div className="flex flex-col justify-between gap-3 sm:flex-row">
                  <div className="space-y-1">
                    <p className="font-semibold">
                      Платёж клиента · Accounting №{record.id}
                    </p>
                    <p className="text-sm text-gray-600">
                      {record.client_name || "Клиент не указан"}
                    </p>
                    <p className="text-sm text-gray-600">
                      Дата: {formatDate(record.date)}
                    </p>
                    {record.payment_agent && (
                      <p className="text-sm text-gray-600">
                        Плательщик / способ: {record.payment_agent}
                      </p>
                    )}
                    {record.comment && (
                      <p className="whitespace-pre-wrap text-sm text-gray-600">
                        {record.comment}
                      </p>
                    )}
                  </div>

                  <div className="min-w-[180px] space-y-1 sm:text-right">
                    <p className="text-sm">
                      Всего: ¥{formatMoney(record.money_sum)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Распределено: ¥{formatMoney(allocated)}
                    </p>
                    <p className="font-bold text-green-700">
                      Остаток: ¥{formatMoney(remaining)}
                    </p>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => openAllocationForm(record)}
                    >
                      Добавить назначение
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    )}
  </div>
</Modal>


);
}
